import * as React from "react";
import Accordion from "react-bootstrap/Accordion";
import { Link } from "react-router-dom";
import "./routes.css";

// Each group renders as its own accordion. Items are {q, a}.
const groups = [
  {
    title: "Your Sessions",
    defaultOpen: "0",
    items: [
      {
        q: "If I'm in pain, how many sessions will it take to resolve?",
        a: (
          <>
            <p>
              <strong>
                That depends on whether your problem is simple or systemic.
              </strong>
            </p>
            <p>
              Chronic pain is usually systemic. If it isn&rsquo;t systemic, one
              session can be enough, with one or two follow up sessions if
              needed. If the problem is systemic, it will require reprogramming
              various layers of malfunctioning muscle patterns which can take
              varying lengths of time.
            </p>
            <p>
              The human body is connected from head to toe in fascial tissue, a
              thin tissue that surrounds every single muscle creating a web
              through the whole body. To understand what happens when muscle
              patterns aren&rsquo;t functioning properly, imagine a tangled
              ball of yarn or a thin, silver, link necklace that someone
              didn&rsquo;t put away at all carefully. To unravel the yarn or
              the necklace (i.e. unknot the muscle), you have to start with
              what&rsquo;s accessible. Sometimes the knots are easy, sometimes
              not. Or, you run into a series of knots. A second knot may be
              worse than a first knot. There are times when everything unravels
              easily and quickly, a miracle session which may resolve the
              problem or, sometimes, uncover more to chip away at. When
              patterns unravel easily, your body is in less pain. When the
              origin of the problem is resolved, muscle patterns hold and pain
              is relieved (i.e. you have useable yarn, a useable necklace).
              Patterns also hold along the way and pain is reduced even if it
              is clear there is more to do.
            </p>
            <p>
              In short, the process of healing can be quick, or it can be long,
              depending on the condition of your body. It didn&rsquo;t get that
              way over night; and the problem may not be resolved over night.
            </p>
          </>
        ),
      },
      {
        q: "How often do I need to come?",
        a: (
          <p>
            Though it isn&rsquo;t always possible, we like to start with three
            sessions in three weeks to get ahead of the problem. We may
            continue on at once a week for awhile or, if things are progressing
            well, we will push the sessions out to every other week, then every
            three weeks, once a month, etc. We determine together whether we
            can put more time in between sessions or if there is too much time
            between sessions, and we back up to less if needed. We work with
            you to discover what the best schedule for your body is and we work
            around financial issues as well.
          </p>
        ),
      },
      {
        q: "Should I come to sessions for the rest of my life?",
        a: (
          <>
            <p>
              <strong>That depends on your goals.</strong>
            </p>
            <p>
              First of all, you have to determine what your goal is. We have
              people who come once a week. We have people who come every other
              week, every three weeks, once a month, a few times a year. We have
              people who come in, get the problem resolved and don&rsquo;t come
              back unless they get into trouble or occasionally come in as a
              treat to themselves.
            </p>
          </>
        ),
      },
      {
        q: "What can I expect from this bodywork?",
        a: (
          <p>
            This bodywork is excellent for regaining and maintaining your
            muscle health and for resolving problems before they start
            &ldquo;talking&rdquo; to you. Muscles can be sore and you
            won&rsquo;t know it until you&rsquo;re getting worked on.
            We&rsquo;ve had people come in saying they feel great. They leave
            saying: I thought I felt good when I came in; now I feel Grrr
            eeaat! So, it depends on your expectation and we stay in
            communication about your goals. Things change and goals may too.
          </p>
        ),
      },
      {
        q: "Will it take care of all my chronic aches and pains?",
        a: (
          <p>
            Not necessarily, but if you have a chronic underlying problem that
            prevents full resolution of the muscle pain problem, then this
            bodywork could be a way to help keep it under control. The choice
            of how much bodywork you receive is always a personal choice, along
            with any mitigating factors; it is based on your personal goals.
          </p>
        ),
      },
      {
        q: "Is there anything else I should know?",
        a: (
          <p>
            In many ways, bodywork is like exercise, i.e. getting into shape.
            You can&rsquo;t go to one exercise class and come out buff. You
            don&rsquo;t take one exercise class and decide that that should do
            it for the rest of your life. Exercise is a process. It takes time
            to get the result you&rsquo;re looking for. It takes time to
            maintain the result. You can stop at different points and not lose
            too much ground, depending on what else you&rsquo;re doing in your
            life, but, if you stop exercising, you will start to lose that tone
            down the road. If you strike a balance and get a decent amount of
            exercise, then, to the degree that you want, you can maintain being
            in shape. Bodywork isn&rsquo;t so different. It is a process.
            You&rsquo;re healthier with it than without it. It&rsquo;s a
            quality of life choice more than anything else, whether
            you&rsquo;re in pain or not. And it plays into how healthy and
            resilient you&rsquo;ll be throughout your life.
          </p>
        ),
      },
    ],
  },
  {
    title: "After a Session",
    items: [
      {
        q: "Will I be sore after the session?",
        a: (
          <>
            <p>
              People often feel great after a session and don&rsquo;t
              experience soreness. And we&rsquo;ve had people feel sore for the
              next day or two, just as you would after taking an exercise class
              your body isn&rsquo;t used to. We recommend taking an Epsom salts
              bath, drinking lots of water and planning your session so you
              don&rsquo;t have anything very demanding you have to do
              afterwards.
            </p>
            <p>
              Being sore after one session doesn&rsquo;t mean you won&rsquo;t
              feel great after your next session or a lot less sore. It is
              entirely dependent on what muscle patterns are resolvable in that
              session, the relative health of the particular muscles
              we&rsquo;ve worked with and how much those muscles are used to
              being taxed during daily activities.
            </p>
          </>
        ),
      },
      {
        q: "What might I experience following a session?",
        a: (
          <p>
            If you have a lot of puffiness in your tissues, your body needs to
            reabsorb it and get rid of it through your excretory system. You
            may be sore after the session because of this and won&rsquo;t feel
            the full benefit of the work until later that evening or in the
            next day or two because proper muscle contraction is what moves
            these fluids out through activation of your lymphatic system. Your
            body has to have time to integrate the work, contract the muscles
            properly, and move the excess fluid out. This is where the soreness
            tends to come from. It is not always a part of the process and it
            typically doesn&rsquo;t follow every session, but it is important
            to be aware of this as a possibility during your healing process.
            Drinking lots of water can help as well as Epsom salt baths.
          </p>
        ),
      },
    ],
  },
  {
    title: "What's Included",
    items: [
      {
        q: "What types of therapies might be used during a session?",
        a: (
          <>
            <p>
              <strong>During a bodywork session:</strong> Reactive Triads™
              Bodywork (with the neuromuscular reprogramming it is based in),
              Lymphatic Drainage, brain work as part of lymph drainage,
              Visceral work, nerve work, energy work including Polarity,
              Shiatsu, Acupressure and Reiki, Somatic Awareness and Energetic
              Attunement, Deep Tissue, Sports Massage, and Swedish.
            </p>
            <p>
              <strong>During a movement session:</strong> Yoga, Dance Movement
              Therapy, Contemplative Movement and Interplay may be integrated
              in any class or individual session.
            </p>
            <p>
              <Link to="/therapies">Read about each therapy ››</Link>
            </p>
          </>
        ),
      },
    ],
  },
];

function FAQ() {
  return (
    <div className="pageBg">
      <div className="container">
        <div className="pageCard">
          <h2 className="pageTitle">Frequently Asked Questions</h2>

          <p className="pageLead">
            What to expect from your sessions, how often to come, and how you
            may feel afterwards.
          </p>

          {groups.map((group) => (
            <React.Fragment key={group.title}>
              <h3 className="sectionTitle">{group.title}</h3>
              <Accordion
                className="therapyAccordion"
                defaultActiveKey={group.defaultOpen}
              >
                {group.items.map((item, i) => (
                  <Accordion.Item eventKey={String(i)} key={item.q}>
                    <Accordion.Header>{item.q}</Accordion.Header>
                    <Accordion.Body>{item.a}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </React.Fragment>
          ))}

          <div className="contactBox">
            <h4 className="subTitle" style={{ marginTop: 0 }}>
              Still have a question?
            </h4>
            <p style={{ marginBottom: 0 }}>
              Call <a href="tel:8605994533">(860) 599-4533</a> or email{" "}
              <a href="mailto:bodymindspiritanatomy@gmail.com">
                bodymindspiritanatomy@gmail.com
              </a>
              . Visits are by appointment only.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
export default FAQ;
